import React from "react";
import {
    Box,
    Flex,
    Text,
    VStack,
    HStack,
    Icon,
    Container,
    SimpleGrid,
    Divider,
    Image
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FaFacebook, FaInstagram, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaClock, FaShieldAlt } from "react-icons/fa";
import logo from "../../assets/images/legalLogo.jpg";
import { useLanguage } from "../../context/LanguageContext";

const Footer = () => {
    const { t } = useLanguage();

    const quickLinks = [
        { to: "/", label: t("nav.home") },
        { to: "/about", label: t("nav.about") },
        { to: "/services", label: t("nav.services") },
        // { to: "/documents", label: "Օրենսդրական Գրադարան" },
        { to: "/contact", label: t("nav.contact") },
    ];

    return (
        <Box
            as="footer"
            bg="#070d1e"
            color="gray.300"
            pt={{ base: 14, md: 18 }}
            pb={8}
            borderTop="1px solid rgba(255, 255, 255, 0.08)"
            position="relative"
            overflow="hidden"
        >
            {/* Ambient background glow */}
            <Box
                position="absolute"
                top="-150px"
                left="50%"
                transform="translateX(-50%)"
                w="600px"
                h="300px"
                bg="radial-gradient(circle, rgba(29, 78, 216, 0.15) 0%, rgba(7, 13, 30, 0) 70%)"
                pointerEvents="none"
            />

            <Container maxW="container.xl" px={{ base: 4, md: 8 }} position="relative" zIndex={1}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={{ base: 10, lg: 8 }} mb={12}>
                    {/* Brand Info */}
                    <VStack align="flex-start" spacing={4}>
                        <RouterLink to="/" style={{ textDecoration: "none" }}>
                            <HStack spacing={3} cursor="pointer">
                                <Box
                                    w={{ base: "44px", md: "50px" }}
                                    h={{ base: "44px", md: "50px" }}
                                    borderRadius="xl"
                                    overflow="hidden"
                                    bg="white"
                                    p={1}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    boxShadow="0 4px 15px rgba(0, 0, 0, 0.35)"
                                    border="1px solid rgba(223, 186, 115, 0.4)"
                                >
                                    <Image
                                        src={logo}
                                        alt="Legal Titan Logo"
                                        maxH="100%"
                                        maxW="100%"
                                        objectFit="contain"
                                    />
                                </Box>
                                <Text fontSize="2xl" fontWeight="800" color="white" letterSpacing="-0.02em">
                                    Legal<Text as="span" color="#dfba73">Titan</Text>
                                </Text>
                            </HStack>
                        </RouterLink>

                        <Text fontSize="sm" lineHeight="1.7" color="gray.400">
                            {t("footer.description")}
                        </Text>

                        <HStack spacing={3} pt={2}>
                            <Box
                                as="a"
                                href="https://www.facebook.com/profile.php?id=61557661694303"
                                target="_blank"
                                rel="noopener noreferrer"
                                w={10}
                                h={10}
                                borderRadius="lg"
                                bg="whiteAlpha.100"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color="gray.300"
                                _hover={{ bg: "#1877f2", color: "white", transform: "translateY(-3px)" }}
                                transition="all 0.25s ease"
                            >
                                <FaFacebook size="18px" />
                            </Box>
                            <Box
                                as="a"
                                href="https://www.instagram.com/legal_titan_llc______/"
                                target="_blank"
                                rel="noopener noreferrer"
                                w={10}
                                h={10}
                                borderRadius="lg"
                                bg="whiteAlpha.100"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color="gray.300"
                                _hover={{ bg: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)", color: "white", transform: "translateY(-3px)" }}
                                transition="all 0.25s ease"
                            >
                                <FaInstagram size="18px" />
                            </Box>
                            <Box
                                as="a"
                                href="mailto:info@legaltitan.am"
                                w={10}
                                h={10}
                                borderRadius="lg"
                                bg="whiteAlpha.100"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color="gray.300"
                                _hover={{ bg: "#ea4335", color: "white", transform: "translateY(-3px)" }}
                                transition="all 0.25s ease"
                            >
                                <FaEnvelope size="18px" />
                            </Box>
                        </HStack>
                    </VStack>

                    {/* Quick Navigation Links */}
                    <VStack align="flex-start" spacing={3}>
                        <Text fontSize="md" fontWeight="700" color="white" mb={1} letterSpacing="0.05em">
                            {t("footer.quickLinks")}
                        </Text>
                        {quickLinks.map(({ to, label }) => (
                            <RouterLink key={to} to={to} style={{ textDecoration: "none" }}>
                                <Text
                                    fontSize="sm"
                                    color="gray.400"
                                    _hover={{ color: "#dfba73", transform: "translateX(4px)" }}
                                    transition="all 0.2s ease"
                                >
                                    → {label}
                                </Text>
                            </RouterLink>
                        ))}
                    </VStack>

                    {/* Key Practice Areas */}
                    <VStack align="flex-start" spacing={3}>
                        <Text fontSize="md" fontWeight="700" color="white" mb={1} letterSpacing="0.05em">
                            {t("footer.practiceAreas")}
                        </Text>
                        {[
                            t("home.practiceAreas.0.title"),
                            t("home.practiceAreas.1.title"),
                            t("home.practiceAreas.2.title"),
                            t("services.items.2.title"),
                            t("services.items.4.title"),
                            t("services.items.6.title"),
                        ].map((item, idx) => (
                            <Text key={idx} fontSize="sm" color="gray.400">
                                • {item}
                            </Text>
                        ))}
                    </VStack>

                    {/* Contact & Hours */}
                    <VStack align="flex-start" spacing={3.5}>
                        <Text fontSize="md" fontWeight="700" color="white" mb={1} letterSpacing="0.05em">
                            {t("footer.contactInfo")}
                        </Text>

                        <HStack align="flex-start" spacing={3}>
                            <Flex w={8} h={8} borderRadius="md" bg="whiteAlpha.100" align="center" justify="center" flexShrink={0} mt={0.5}>
                                <Icon as={FaPhoneAlt} color="#dfba73" fontSize="13px" />
                            </Flex>
                            <Box fontSize="sm">
                                <Text as="a" href="tel:+37477531309" display="block" color="gray.300" _hover={{ color: "white" }}>
                                    +374 77 53 13 09
                                </Text>
                                <Text as="a" href="tel:+37455673001" display="block" color="gray.400" _hover={{ color: "white" }}>
                                    +374 55 67 30 01
                                </Text>
                            </Box>
                        </HStack>

                        <HStack align="flex-start" spacing={3}>
                            <Flex w={8} h={8} borderRadius="md" bg="whiteAlpha.100" align="center" justify="center" flexShrink={0}>
                                <Icon as={FaEnvelope} color="#dfba73" fontSize="13px" />
                            </Flex>
                            <Text as="a" href="mailto:info@legaltitan.am" fontSize="sm" color="gray.300" _hover={{ color: "white" }}>
                                info@legaltitan.am
                            </Text>
                        </HStack>

                        <HStack align="flex-start" spacing={3}>
                            <Flex w={8} h={8} borderRadius="md" bg="whiteAlpha.100" align="center" justify="center" flexShrink={0}>
                                <Icon as={FaMapMarkerAlt} color="#dfba73" fontSize="14px" />
                            </Flex>
                            <Text fontSize="sm" color="gray.300">
                                {t("connect.addressValue")}
                            </Text>
                        </HStack>

                        <HStack align="flex-start" spacing={3}>
                            <Flex w={8} h={8} borderRadius="md" bg="whiteAlpha.100" align="center" justify="center" flexShrink={0}>
                                <Icon as={FaClock} color="#dfba73" fontSize="13px" />
                            </Flex>
                            <Box fontSize="sm">
                                <Text color="gray.300">{t("connect.daysValue")}</Text>
                                <Text color="gray.400" fontSize="xs">{t("connect.hoursValue")}</Text>
                            </Box>
                        </HStack>
                    </VStack>
                </SimpleGrid>

                <Divider borderColor="whiteAlpha.100" my={6} />

                <Flex
                    direction={{ base: "column", sm: "row" }}
                    justify="space-between"
                    align="center"
                    fontSize="xs"
                    color="gray.400"
                    gap={3}
                >
                    <Text>
                        © {new Date().getFullYear()} {t("footer.copyright")}
                    </Text>
                    <HStack spacing={2}>
                        <Icon as={FaShieldAlt} color="brand.400" />
                        <Text color="gray.400">{t("footer.badgeProtection")}</Text>
                    </HStack>
                </Flex>
            </Container>
        </Box>
    );
};

export default Footer;